import { InheritedConfig, IniInfo, PackageConfigSection } from "system/types/interfaces";
import { isFullPatternMatch } from "./stringExtensions";

/**
 * Processes a list of rules by handling trailing comments.
 *
 * Each rule's trailing comments, if present, are split, trimmed, and treated as separate rules
 * with generated names based on the original rule's name and index.
 *
 * @param rules - An array of IniInfo objects representing the rules to be processed.
 * @returns The processed list of IniInfo objects, including original rules and their comments as separate entries.
 */
export const processRules = (rules: IniInfo[]): IniInfo[] => {
  const processedRules: IniInfo[] = [];

  const ruleMap: Record<string, IniInfo> = {};
  const duplicates: { ruleString: string; count: number }[] = [];

  rules.forEach((rule) => {
    const ruleString = `${rule.name} ${rule.delimiter} ${rule.value}`.trim();
    if (ruleMap[ruleString]) {
      const existing = duplicates.find((d) => d.ruleString === ruleString);
      if (existing) {
        existing.count += 1;
      } else {
        duplicates.push({ ruleString, count: 2 });
      }
    } else {
      ruleMap[ruleString] = rule;
    }
  });

  if (duplicates.length > 0) {
    console.log("Duplicate rules detected and removed during processing:");
    duplicates.forEach((duplicate) => {
      console.log(`- Rule: "${duplicate.ruleString}" (Found ${duplicate.count} times, kept only the first occurrence)`);
    });
  }

  const uniqueRules = Object.values(ruleMap);
  processedRules.push(...uniqueRules);

  return processedRules;
};

export function processFinalRules(rules: IniInfo[]): IniInfo[] {
  let clonedRules = rules.filter((rule) => !rule.isDeleted).map((r) => ({ ...r }));

  clonedRules = clonedRules.map((rule) => {
    if (rule.inlineComment === "" || (typeof rule.inlineComment === "string" && rule.inlineComment.trim() === "")) {
      rule.inlineComment = null;
    }
    return rule;
  });

  return clonedRules;
}

export const isInheritanceName = (name: string, packages: IniInfo[]) => {
  // Step 1: Check for regex characters
  const hasRegexChars = /[\(\)\|\*\+\?\{\}]/.test(name); // Only true for regex-specific chars

  // Step 3: Check number of matches with isFullPatternMatch(true) - regex match
  const regexMatches = packages.filter((pkg) => pkg.value && isFullPatternMatch(pkg.value, name, true)).length;

  // Logic: If has regex chars and matches multiple with regex, or literal match is 1 but regex match >1, it's inheritance
  if (name.includes("|") && hasRegexChars && regexMatches >= 1) return true;
  if (regexMatches > 1) return true;
  return false;
};

export const splitConfigurations = (packageConfigSections: PackageConfigSection[], packages: IniInfo[]) => {
  const normal: PackageConfigSection[] = [];
  const inherited: InheritedConfig[] = [];

  packageConfigSections.forEach((config, index) => {
    const cleanLines = config.lines.map((line) => ({ ...line, pendingChanges: false }));
    if (isInheritanceName(config.name, packages)) {
      const matchingPackages = packages
        .filter((pkg) => pkg.value && isFullPatternMatch(pkg.value, config.name, true))
        .map((pkg) => pkg.value);

      inherited.push({
        id: index + 1,
        regex: config.name,
        matchingPackages,
        configurations: cleanLines,
        notConfiguredRules: [],
        pendingChanges: false,
        isDeleted: false,
      });
    } else {
      normal.push({ ...config, lines: cleanLines, inheritance: false });
    }
  });

  return { normal, inherited };
};

